import { Button, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import "../user/LoginDialog.css";
import "./AuthStatusView.css";
import type { AuthErrorReason } from "./AuthSessionOrchestrator";

export type AuthStatus = "authenticating" | "logging-out" | "redirecting-to-login" | "error";

interface AuthStatusViewProps {
  status: AuthStatus;
  reason?: AuthErrorReason;
  message?: string;
  onRetry?: () => void;
}

const STATUS_COPY: Record<AuthStatus, { titleKey: string; bodyKey: string }> = {
  authenticating: {
    titleKey: "auth.authenticating",
    bodyKey: "auth.authenticatingBody",
  },
  "logging-out": {
    titleKey: "auth.loggingOut",
    bodyKey: "auth.loggingOutBody",
  },
  "redirecting-to-login": {
    titleKey: "auth.redirectingToLogin",
    bodyKey: "auth.redirectingToLoginBody",
  },
  error: {
    titleKey: "auth.authFailed",
    bodyKey: "auth.authFailedBody",
  },
};

const ERROR_COPY: Record<AuthErrorReason, string> = {
  "auth0-callback-error": "auth.auth0CallbackError",
  "login-redirect-failed": "auth.loginRedirectFailed",
  "missing-auth0-id-token": "auth.missingAuth0IdToken",
  "token-exchange-failed": "auth.tokenExchangeFailed",
};

/**
 * Renders the local auth/session status surface.
 *
 * Use this for authentication progress, logout, redirect, and retryable error
 * states. The caller chooses the display status and supplies the retry callback
 * when a failed flow can be retried.
 *
 * @param status Status view to render.
 * @param reason Auth error reason to display when `status` is `error`.
 * @param message Error message to display when `status` is `error`.
 * @param onRetry Optional callback for retrying a failed auth flow.
 */
export function AuthStatusView({ status, reason, message, onRetry }: AuthStatusViewProps) {
  const { t } = useTranslation();
  const copy = STATUS_COPY[status];
  const isError = status === "error";
  const errorMessage = reason ? t(ERROR_COPY[reason]) : (message ?? t(copy.bodyKey));

  return (
    <Modal
      backdropClassName="auth-status-backdrop"
      className="auth-status-modal"
      show
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header className="custom-modal-header">
        <Modal.Title>{t(copy.titleKey)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isError ? (
          <div className="alert alert-danger" role="alert">
            {errorMessage}
          </div>
        ) : (
          <div className="text-center">
            <Spinner animation="border" role="status" aria-label={t(copy.titleKey)} />
            <p className="mt-3 mb-0">{t(copy.bodyKey)}</p>
          </div>
        )}
        {onRetry && (
          <div className="d-flex justify-content-end mt-3">
            <Button onClick={onRetry} variant="primary">
              {t("auth.tryAgain")}
            </Button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
